type ApiOptions = RequestInit & {
  json?: any;
  dedupeKey?: string;      // single-flight key
  retries?: number;        // network retry attempts
  retryDelayMs?: number;   // backoff base
  silentAbort?: boolean;   // don't throw/notify on AbortError
};

const inflight = new Map<string, Promise<any>>();

function sleep(ms: number) { return new Promise(r => setTimeout(r, ms)); }

export async function api<T = any>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { json, dedupeKey = path, retries = 1, retryDelayMs = 500, silentAbort = true, ...rest } = opts;

  // Single-flight: reuse the same promise if request is already in progress.
  if (inflight.has(dedupeKey)) return inflight.get(dedupeKey)! as Promise<T>;

  const controller = new AbortController();
  const signal = rest.signal ?? controller.signal;

  const run = async (): Promise<T> => {
    try {
      const res = await fetch(path, {
        credentials: 'include',
        headers: json ? { 'content-type': 'application/json', ...(rest.headers||{}) } : rest.headers,
        body: json ? JSON.stringify(json) : rest.body,
        signal,
        ...rest,
      });

      // Handle 401 → reauth flow
      if (res.status === 401) {
        // Optionally: show one toast, then redirect to consent
        window.dispatchEvent(new CustomEvent('app:reauth', { detail: { provider: 'google' }}));
        throw new Error('reauth_required');
      }

      if (!res.ok) {
        const text = await res.text().catch(()=>'');
        throw new Error(text || `HTTP ${res.status}`);
      }
      const type = res.headers.get('content-type') || '';
      return type.includes('application/json') ? await res.json() : (await res.text() as any);
    } catch (err: any) {
      // AbortError should be silent (we abort on route change, visibility, etc.)
      if (silentAbort && (err?.name === 'AbortError' || `${err}`.includes('AbortError'))) {
        // swallow
        throw Object.assign(new Error('abort'), { abort: true });
      }

      // Network issues → limited retries
      if (`${err}`.includes('Failed to fetch') && retries > 0) {
        await sleep(retryDelayMs);
        return api<T>(path, { ...opts, retries: retries - 1, retryDelayMs: retryDelayMs * 2 });
      }
      throw err;
    }
  };

  const p = run().finally(() => inflight.delete(dedupeKey));
  inflight.set(dedupeKey, p);
  return p;
}

// Legacy export for backward compatibility - parses Response objects
export async function j<T = any>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `HTTP ${response.status}`);
  }
  const type = response.headers.get('content-type') || '';
  return type.includes('application/json') ? await response.json() : (await response.text() as any);
}